import React from "react";
import {
  Grid,
  Paper,
  Stack,
  Box,
  Typography,
  Switch,
  TextField,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Divider,
  Button,
  Chip,
  Tooltip,
  IconButton,
  Alert,
} from "@mui/material";
import { Schedule, AccessTime, Preview } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { DAY_LABELS } from "./LaunchVoiceCampaign";
import { sectionHeaderSx } from "./LaunchVoiceCampaign";

export default function BatchingSettings({ settings, onChange }) {
  // Destructure with safe defaults
  const {
    enabled = false,
    batchSize = "",
    interval = "",
    activeDays = [],
    nonActiveHours = [],
  } = settings || {};

  // Helper to update a specific field in the parent's state
  const updateSettings = (updates) => {
    onChange({ ...settings, ...updates });
  };

  const toggleEnabled = (e) => {
    if (e) e.stopPropagation();
    updateSettings({ enabled: !enabled });
  };

  const handleActiveDayToggle = (idx, isChecked) => {
    const newActiveDays = isChecked
      ? [...activeDays, idx]
      : activeDays.filter((d) => d !== idx);
    updateSettings({ activeDays: newActiveDays });
  };

  const addBlackoutWindow = () => {
    updateSettings({
      nonActiveHours: [...nonActiveHours, { start: "22:00", end: "08:00" }],
    });
  };

  const updateBlackoutWindow = (index, field, value) => {
    const newHours = [...nonActiveHours];
    newHours[index] = { ...newHours[index], [field]: value };
    updateSettings({ nonActiveHours: newHours });
  };

  const removeBlackoutWindow = (index) => {
    const newHours = nonActiveHours.filter((_, i) => i !== index);
    updateSettings({ nonActiveHours: newHours });
  };

  return (
    <Grid item xs={12}>
      <Paper
        sx={{
          overflow: "hidden",
          border: enabled
            ? "1px solid rgba(59,130,246,0.4)"
            : "1px solid #e5e7eb",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ ...sectionHeaderSx, cursor: "pointer" }}
          onClick={toggleEnabled}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Schedule sx={{ color: "#3b82f6" }} />
            <Box>
              <Typography fontWeight={600}>Batching Settings</Typography>
              <Typography variant="caption" color="text.secondary">
                Split campaign into batches with interval and daily schedule
              </Typography>
            </Box>
          </Stack>
          <Switch checked={enabled} onChange={toggleEnabled} color="primary" />
        </Stack>

        {enabled && (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3} mb={4}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Batch Size"
                  type="number"
                  size="small"
                  value={batchSize}
                  onChange={(e) =>
                    updateSettings({ batchSize: e.target.value })
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">contacts</InputAdornment>
                    ),
                  }}
                  helperText="Contacts per batch (recommended ≤ 10,000)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Interval Between Batches"
                  type="number"
                  size="small"
                  value={interval}
                  onChange={(e) => updateSettings({ interval: e.target.value })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">minutes</InputAdornment>
                    ),
                  }}
                  helperText="Delay before next batch starts"
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle1" fontWeight={600} mb={1}>
              Active Days of the Week
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Batches will only run on selected days
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 4 }}>
              {DAY_LABELS.map((day, idx) => (
                <FormControlLabel
                  key={idx}
                  control={
                    <Checkbox
                      checked={activeDays.includes(idx)}
                      size="small"
                      onChange={(e) =>
                        handleActiveDayToggle(idx, e.target.checked)
                      }
                    />
                  }
                  label={day}
                />
              ))}
            </Box>

            <Divider sx={{ my: 3 }} />

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  Daily Non-Active Hours (Blackout)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No batches will run during these windows. You can add multiple
                  periods.
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={addBlackoutWindow}
              >
                Add Window
              </Button>
            </Stack>

            <Stack spacing={2} mt={2}>
              {nonActiveHours.map((win, idx) => (
                <Paper
                  key={idx}
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "#fafafa",
                    borderColor: "#e5e7eb",
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    flexWrap="wrap"
                  >
                    <AccessTime sx={{ color: "#6b7280", fontSize: 20 }} />
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{ minWidth: 60 }}
                    >
                      Window {idx + 1}
                    </Typography>
                    <TextField
                      label="From"
                      type="time"
                      size="small"
                      value={win.start}
                      onChange={(e) =>
                        updateBlackoutWindow(idx, "start", e.target.value)
                      }
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 150 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      to
                    </Typography>
                    <TextField
                      label="To"
                      type="time"
                      size="small"
                      value={win.end}
                      onChange={(e) =>
                        updateBlackoutWindow(idx, "end", e.target.value)
                      }
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 150 }}
                    />
                    <Chip
                      size="small"
                      label={win.start > win.end ? "Overnight" : "Same day"}
                      color={win.start > win.end ? "warning" : "default"}
                      variant="outlined"
                    />
                    <Tooltip title="Remove this blackout window">
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeBlackoutWindow(idx)}
                          disabled={nonActiveHours.length === 1}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Paper>
              ))}
            </Stack>

            <Alert severity="info" sx={{ mt: 2 }}>
              Example: <strong>22:00 – 08:00</strong> = no activity overnight •{" "}
              <strong>13:00 – 14:00</strong> = no activity during lunch
            </Alert>

            <Box sx={{ mt: 3 }}>
              <Alert severity="success" icon={<Preview />}>
                When you launch, a <strong>batch preview</strong> will be shown
                before the campaign starts — including exact send times for each
                batch.
              </Alert>
            </Box>
          </Box>
        )}
      </Paper>
    </Grid>
  );
}
